import { Injectable, computed, signal } from '@angular/core';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'arco-theme';

/**
 * Tema de la aplicacion: oscuro (el lienzo azul de siempre) o claro.
 *
 * Se aplica poniendo `data-theme` en <html>, que es donde viven los tokens
 * (`_tokens.scss`). Todo el color de la app sale de esas variables, asi que
 * el cambio es una sola sustitucion de valores y no hay que tocar pantalla
 * por pantalla.
 *
 * La eleccion se guarda en localStorage. Sin eleccion previa, arranca en
 * oscuro siempre: es el tema historico de la app y el que se probo primero,
 * y dejarlo a merced de `prefers-color-scheme` significaba que alguien con
 * el sistema operativo en modo claro -el default de fabrica en la mayoria
 * de los equipos- veia el tema claro sin haberlo elegido nunca.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _theme = signal<Theme>(this.leerPreferencia());

  readonly theme = this._theme.asReadonly();
  readonly esOscuro = computed(() => this._theme() === 'dark');

  constructor() {
    this.aplicar(this._theme());
  }

  alternar(): void {
    this.usar(this._theme() === 'dark' ? 'light' : 'dark');
  }

  usar(theme: Theme): void {
    this._theme.set(theme);
    this.aplicar(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Almacenamiento bloqueado (modo privado): vale para esta sesion.
    }
  }

  private aplicar(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
    // Pinta los controles nativos del navegador (scrollbars, selects, date
    // pickers) del lado correcto; sin esto quedan claros sobre el oscuro.
    document.documentElement.style.colorScheme = theme;
  }

  private leerPreferencia(): Theme {
    try {
      const guardado = localStorage.getItem(STORAGE_KEY);
      if (guardado === 'dark' || guardado === 'light') return guardado;
    } catch {
      // Sin almacenamiento tambien cae al default de abajo.
    }
    return 'dark';
  }
}
